import { TourTaskState, VehicleType } from "../../api/types";
import isEqual from "react-fast-compare";
import {
  ErrorTourDetailAction,
  TourDetailAction,
  TourDetailState,
  UpdateLoadedTourDetailAction,
  UpdatePickupCanceledAction,
  UpdateTourStateAction,
  UpdateTourStopStateAction,
} from "../types";
import { determineTourStopState } from "../utils/shared";
import { determineBulkTaskState } from "../bulk";

export const initialState: TourDetailState = {
  tour: {
    id: "",
    name: "",
    state: "",
    startLocation: "",
    endLocation: {
      address: "",
      name: "",
    },
    stopCount: 0,
    vehicle: VehicleType.NONE,
  },
  stops: [],
  loading: false,
  error: null,
};

export function tourDetailReducer(
  state: TourDetailState,
  action: TourDetailAction,
): TourDetailState {
  switch (action.type) {
    case "new": {
      if (isEqual(state, action.newState)) {
        return state;
      }

      return action.newState;
    }

    case "error": {
      return handleErrorAction(state, action);
    }

    case "updateTourState": {
      return handleUpdateTourStateAction(state, action);
    }

    case "updateTourStopState": {
      return handleUpdateTourStopAction(state, action);
    }

    case "updateLoaded": {
      return handleLoadedAction(state, action);
    }

    case "updatePickupCanceled": {
      return handlePickupCanceledAction(state, action);
    }

    default: {
      return state;
    }
  }
}

function handleUpdateTourStateAction(
  state: TourDetailState,
  action: UpdateTourStateAction,
): TourDetailState {
  return {
    ...state,
    tour: {
      ...state.tour,
      state: action.newState,
    },
  };
}

export function handleUpdateTourStopAction(
  state: TourDetailState,
  action: UpdateTourStopStateAction,
): TourDetailState {
  const updatedStops = state.stops.map((stop) => {
    const updatedItems = stop.items.map((item) => {
      if (item.id === action.stopId) {
        return { ...item, state: action.newState };
      }

      if (item.isBulkGroup && item.bulkTasks) {
        const updatedBulkTasks = item.bulkTasks.map((bulkTask) => {
          if (bulkTask.id === action.stopId) {
            return { ...bulkTask, state: action.newState };
          }
          return bulkTask;
        });

        const hasUpdatedBulkTask = updatedBulkTasks.some(
          (task, index) => task.state !== item.bulkTasks![index].state,
        );

        if (hasUpdatedBulkTask) {
          return {
            ...item,
            bulkTasks: updatedBulkTasks,
            state: determineBulkTaskState(updatedBulkTasks),
          };
        }
      }

      return item;
    });

    return {
      ...stop,
      items: updatedItems,
      state: determineTourStopState(updatedItems),
    };
  });

  return {
    ...state,
    stops: updatedStops,
  };
}

function handleErrorAction(
  state: TourDetailState,
  action: ErrorTourDetailAction,
): TourDetailState {
  return {
    ...state,
    loading: false,
    error: action.error,
  };
}

export function handleLoadedAction(
  state: TourDetailState,
  action: UpdateLoadedTourDetailAction,
): TourDetailState {
  return {
    ...state,
    stops: state.stops.map((stop) => {
      const updatedItems = stop.items.map((item) => {
        if (item.id === action.taskId) {
          return { ...item, state: "done" as TourTaskState };
        }

        if (item.parentId === action.parentId) {
          return { ...item, state: "loaded" as TourTaskState };
        }

        if (item.isBulkGroup && item.bulkTasks) {
          const updatedBulkTasks = item.bulkTasks.map((bulkTask) => {
            if (bulkTask.id === action.taskId) {
              return { ...bulkTask, state: "done" as TourTaskState };
            }

            if (bulkTask.parentId === action.parentId) {
              return { ...bulkTask, state: "loaded" as TourTaskState };
            }

            return bulkTask;
          });

          const hasUpdatedBulkTask = updatedBulkTasks.some(
            (task, index) => task.state !== item.bulkTasks![index].state,
          );

          if (hasUpdatedBulkTask) {
            return {
              ...item,
              bulkTasks: updatedBulkTasks,
              state: determineBulkTaskState(updatedBulkTasks),
            };
          }
        }

        return item;
      });

      return {
        ...stop,
        state: determineTourStopState(updatedItems),
        items: updatedItems,
      };
    }),
  };
}

function handlePickupCanceledAction(
  state: TourDetailState,
  action: UpdatePickupCanceledAction,
): TourDetailState {
  return {
    ...state,
    stops: state.stops.map((stop) => {
      const updatedItems = stop.items.map((item) => {
        // Cancel the pickup task
        if (item.id === action.taskId) {
          return { ...item, state: "canceled" as TourTaskState };
        }

        // Cancel the parent dropoff task
        if (item.parentId === action.parentId) {
          return { ...item, state: "canceled" as TourTaskState };
        }

        return item;
      });

      return {
        ...stop,
        state: determineTourStopState(updatedItems),
        items: updatedItems,
      };
    }),
  };
}
